import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FDStack, FDBodyText, FDBox } from '@facet-wealth/design';
import { noop } from 'Helpers/PropTypes';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import MessageSkeleton from 'Components/ComponentLibrary/Atoms/Skeletons/MessageSkeleton';
import ResponseButtons
  from 'Components/ComponentLibrary/Organisms/MessageList/MessageListItem/MessageContent/Content/MessageWithResponseButtonsContent/ResponseButtons';
import Sentry from 'Integrations/Sentry';
import { useSnackbarContext } from 'Context/SnackbarContext';
import DataContainer from 'Components/UI/DataContainer';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { navigateLinkToClientPage } from 'Helpers/Navigation';
import RichTextDisplayPicker
  from 'Components/ComponentLibrary/Organisms/MessageList/MessageListItem/MessageContent/Content/RichTextDisplayPicker';


const MessageWithResponseButtonsContent = ({
  messageResource,
  onLoadComplete,
}) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { displayErrorMessage } = useSnackbarContext();
  const [isLoading, setIsLoading] = useState(false);
  const [innerMessageResponseResource, setInnerMessageResponseResource] = useState(null);


  const handleMessageResponse = async (e, relationship) => {
    logStandardizedEvent(categories.clientMessages,
      eventTypes.clickButton,
      `${messageResource.attr('messageType')} Message - Clicked Response Button - ${relationship.meta('prompt')}`, {
        eventProperties: {
          messageId: messageResource.id(),
        },
      });
    try {
      const [interimResponse] = await relationship.link('related').get();
      const [newInnerMessageResponseResource] = await interimResponse.submit();
      setInnerMessageResponseResource(newInnerMessageResponseResource);
      if (newInnerMessageResponseResource.relationship(fr.relationships.postResponseRedirect.name)) {
        await navigateLinkToClientPage(newInnerMessageResponseResource.relationship(fr.relationships.postResponseRedirect.name).link('related'));
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const fetchInnerMessageResponseResource = async () => {
    try {
      const innerMessageResponseResourceFromRelationship = await messageResource.withRelationship(fr.relationships.innerMessageResponse.name,
        async () => {
          const [withRelationshipInnerMessageResponseResource] = await messageResource.relationship(fr.relationships.innerMessageResponse.name).link('related').get();
          return withRelationshipInnerMessageResponseResource;
        },
        () => null);
      setInnerMessageResponseResource(innerMessageResponseResourceFromRelationship);
    } catch (err) {
      Sentry.createSentryErrorLog(err);
    }
  };

  useComponentDidMount(async () => {
    setIsLoading(true);
    await fetchInnerMessageResponseResource();
    setIsLoading(false);
    onLoadComplete();
  });

  return (
    <DataContainer
      loading={isLoading}
      skeleton={MessageSkeleton}
      component={() => (
        <FDStack spacing={2} direction={'column'}>
          <RichTextDisplayPicker content={messageResource.attr('content')} resource={messageResource} />
          {innerMessageResponseResource ? (
            <FDBox paddingY={2}>
              <ResponseButtons
                innerMessageResponseResource={innerMessageResponseResource}
                handleMessageResponse={handleMessageResponse}
              />
            </FDBox>
          ) : null}
          {innerMessageResponseResource?.attr('memberResponseAcknowledgement') ? (
            <FDBodyText value={innerMessageResponseResource.attr('memberResponseAcknowledgement')} />
          ) : null
           }
        </FDStack>
      )}
    />
  );
};

MessageWithResponseButtonsContent.defaultProps = {
  onLoadComplete: noop,
};

MessageWithResponseButtonsContent.propTypes = {
  messageResource: PropTypes.object.isRequired,
  onLoadComplete: PropTypes.func,
};

export default MessageWithResponseButtonsContent;
